import { useState, useEffect } from "react";
import { ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useLang } from "../../i18n/LanguageContext";
import Reveal from "../shared/Reveal";
import GlowCTA from "../shared/effects/GlowCTA";

export const BrandStatement = () => {
  const { lang } = useLang();

  const disciplines_en = ["AI", "COMMERCE", "CONSULTANCY", "MARKETING", "GROWTH", "CREATIVE"];
  const disciplines_ar = ["الذكاء الاصطناعي", "التجارة", "الاستشارات", "التسويق", "النمو", "الإبداع"];
  const words = lang === "en" ? disciplines_en : disciplines_ar;
  const [wordIdx, setWordIdx] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setWordIdx((i) => (i + 1) % words.length), 2200);
    return () => clearInterval(id);
  }, [words.length]);

  // Live GST clock
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  const gstTime = new Date(now.getTime() + (now.getTimezoneOffset() + 240) * 60000);
  const timeStr = gstTime.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", second: "2-digit" });

  const manifesto_en = [
    "STRATEGY IS CHEAP. EXECUTION IS EVERYTHING.",
    "◆",
    "DESIGNED IN DUBAI. DEPLOYED WORLDWIDE.",
    "◆",
    "AI IS OPERATING LEVERAGE.",
    "◆",
    "SHIP WEEKLY. COMPOUND FOREVER.",
    "◆",
  ];
  const manifesto_ar = [
    "الاستراتيجية رخيصة. التنفيذ هو كل شيء.",
    "◆",
    "صُمِّم في دبي. نُشِر عالمياً.",
    "◆",
    "الذكاء الاصطناعي رافعة تشغيل.",
    "◆",
    "أصدر أسبوعياً. انمُ إلى الأبد.",
    "◆",
  ];
  const manifestoPhrases = [...Array(3)].flatMap(() => (lang === "en" ? manifesto_en : manifesto_ar));

  return (
    <section
      className="relative py-24 md:py-32 overflow-hidden"
      data-testid="brand-statement"
      aria-label="Gulf TechLab brand statement"
    >
      {/* Backdrop: floating glow orbs (grid comes from the global BackgroundGrid layer) */}
      <div className="absolute -top-24 left-1/4 w-96 h-96 bg-cyan-500/15 rounded-full blur-[140px] pointer-events-none" />
      <div className="absolute -bottom-24 right-1/4 w-[500px] h-[500px] bg-blue-600/10 rounded-full blur-[160px] pointer-events-none" />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 md:px-8">
        {/* Telemetry strip */}
        <div className="flex flex-wrap items-center justify-between gap-y-3 gap-x-6 pb-7 border-b border-hair text-[10px] mono-label">
          <div className="flex items-center gap-5 text-faint">
            <span className="flex items-center gap-1.5" data-testid="status-live">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
              </span>
              <span className="text-emerald-400">LIVE</span>
            </span>
            <span>LAT 25.2048°N</span>
            <span>LONG 55.2708°E</span>
            <span className="hidden sm:inline">DUBAI · UAE</span>
          </div>
          <div className="flex items-center gap-5 text-faint">
            <span className="tabular-nums">{timeStr} GST</span>
            <span>EST. 2026</span>
            <span className="hidden sm:inline">v1.0.0</span>
          </div>
        </div>

        {/* Hero wordmark — SVG auto-fits */}
        <Reveal variant="scale" duration={1200} className="relative select-none py-12 md:py-16" data-testid="brand-wordmark-container">
          {/* Shimmer sweep */}
          <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-40 pointer-events-none overflow-hidden">
            <div
              className="absolute top-0 h-full w-[30%]"
              style={{
                background: "linear-gradient(90deg, transparent 0%, rgba(0,229,255,0.35) 50%, transparent 100%)",
                filter: "blur(48px)",
                animation: "footer-sweep 7s linear infinite",
              }}
            />
          </div>

          <svg
            viewBox="0 0 1000 130"
            preserveAspectRatio="xMidYMid meet"
            className="relative w-full h-auto"
            aria-label="Gulf TechLab"
            data-testid="brand-wordmark"
          >
            <defs>
              <linearGradient id="gtl-wordmark-body" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="var(--text-strong)" />
                <stop offset="50%" stopColor="#00AEEF" />
                <stop offset="100%" stopColor="#007AFF" />
              </linearGradient>
            </defs>
            <text
              x="500"
              y="105"
              textAnchor="middle"
              fontFamily="'Outfit', sans-serif"
              fontWeight="800"
              fontSize="125"
              letterSpacing="-4"
              fill="url(#gtl-wordmark-body)"
            >
              GULF TECHLAB
            </text>
          </svg>

          <div className="absolute left-0 bottom-6 hidden md:flex items-center gap-2 text-[10px] mono-label text-cyan-300/60">
            <span className="w-6 h-px bg-cyan-400/40" />
            <span>001 / WORDMARK</span>
          </div>
          <div className="absolute right-0 bottom-6 hidden md:flex items-center gap-2 text-[10px] mono-label text-cyan-300/60">
            <span>UAE · GCC · MENA</span>
            <span className="w-6 h-px bg-cyan-400/40" />
          </div>
        </Reveal>

        {/* Rotating discipline tagline */}
        <div className="relative flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-center">
          <span className="text-base md:text-lg text-mid">
            {lang === "en" ? "We ship" : "ننفّذ في"}
          </span>
          <span
            className="relative inline-block h-7 md:h-8 overflow-hidden align-middle"
            style={{ minWidth: lang === "en" ? "170px" : "200px" }}
            data-testid="rotating-discipline"
          >
            {words.map((w, i) => {
              const state =
                i === wordIdx
                  ? "active"
                  : i === (wordIdx - 1 + words.length) % words.length
                  ? "prev"
                  : "next";
              return (
                <span
                  key={i}
                  className={`absolute inset-0 flex items-center justify-center font-mono text-base md:text-lg font-bold tracking-[0.18em] text-cyan-300 transition-all duration-500 ${
                    state === "active"
                      ? "opacity-100 translate-y-0"
                      : state === "prev"
                      ? "opacity-0 -translate-y-full"
                      : "opacity-0 translate-y-full"
                  }`}
                  style={{ textShadow: "0 0 20px rgba(0,229,255,0.5)" }}
                >
                  {w}
                </span>
              );
            })}
          </span>
          <span className="text-base md:text-lg text-mid">
            {lang === "en" ? "for ambitious teams." : "للفرق الطموحة."}
          </span>
        </div>

        {/* Manifesto marquee */}
        <div className="relative mt-14 py-6 border-y border-hair overflow-hidden">
          <div className="flex gap-10 animate-marquee whitespace-nowrap">
            {manifestoPhrases.map((phrase, i) => (
              <span
                key={i}
                className={`text-sm mono-label tracking-[0.2em] ${
                  phrase === "◆" ? "text-cyan-400" : "text-faint"
                }`}
              >
                {phrase}
              </span>
            ))}
          </div>
        </div>

        {/* CTA row */}
        <div className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-4">
          <GlowCTA
            to="/contact"
            data-testid="brand-statement-cta"
            className="bg-cyan-400 hover:bg-cyan-300 text-slate-950 font-semibold px-6 py-3.5 rounded-xl shadow-[0_0_32px_rgba(0,229,255,0.35)] hover:shadow-[0_0_48px_rgba(0,229,255,0.55)] transition-colors"
          >
            {lang === "en" ? "Build with us" : "ابنِ معنا"}
            <ArrowUpRight className="w-4 h-4" />
          </GlowCTA>
          <Link
            to="/about"
            className="inline-flex items-center gap-2 glass hover:border-cyan-400/50 text-strong font-medium px-6 py-3.5 rounded-xl transition-all"
          >
            {lang === "en" ? "Our manifesto" : "مبادئنا"}
            <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default BrandStatement;
